import React, {useEffect, useState} from 'react'
import {TicketDetailDTO} from 'models/Ticket'
import {
 getTicketDetailApi,
 updateTicketApi,
} from 'utils/api/auth/ticketService'
import {Spinner} from 'helpers'
import {useNavigate} from 'react-router-dom'
import ReopenTicketModal from './ReopenTicketModal'
import ResolutionCommentModal from './ResolutionCommentModal'
import ResolutionFeedback from './ResolutionFeedback'
import {formatDate} from 'utils/helper'
import {useTranslation} from 'react-i18next'
import {USER_CATEGORY} from 'constants/userCategory'

type Props = {
 ticketId: string
}
type PreviewAttachment = {
 id: string
 type: 'image' | 'video'
 url: string
}

const ROLE_GROUPS = {
 PMU: USER_CATEGORY.PMU_BAYER,
 DISTRIBUTOR: USER_CATEGORY.DISTRIBUTOR_DIRECTOR,
 TSU: USER_CATEGORY.TSU_USER,
} as const

const TicketDetailData = ({ticketId}: Props) => {
 const navigate = useNavigate()
 const {t} = useTranslation()
 const [data, setData] = useState<TicketDetailDTO | null>(
  null
 )
 const [loading, setLoading] = useState(true)
 const userAccess = localStorage.getItem('user')
 const [remark, setRemark] = useState('')
 const isOpen = data && data.ticket_status === 'OPEN'
 const isReopen = data && data.ticket_status === 'RE_OPEN'
 const isClosed = data && data.ticket_status === 'CLOSED'
 const isResolved =
  data && data.ticket_status === 'RESOLVED'
 const [reOpenModal, setReOpenModal] = useState(false)
 const [resolveModal, setResolveModal] = useState(false)
 const [resolutionComment, setResolutionComment] =
  useState('')
 const userCategoryKey = userAccess
  ? JSON.parse(userAccess)?.userCategoryKey
  : null
 const [preview, setPreview] =
  useState<PreviewAttachment | null>(null)
 const shouldShowResolutionFeedback =
  data?.ticket_number &&
  isClosed &&
  userCategoryKey !== ROLE_GROUPS.TSU &&
  userCategoryKey === ROLE_GROUPS.PMU &&
  userAccess &&
  data?.ticket_raised_by === JSON.parse(userAccess)?.name

 const handleReOpenClick = async () => {
  setReOpenModal(true)
 }

 const handleReOpenSubmit = async () => {
  setLoading(true)

  const res = await updateTicketApi(ticketId, {
   status: 'RE_OPEN',
   reopen_reason: remark,
  })

  setLoading(false)

  if (res.success) {
   setReOpenModal(false)

   navigate(-1)
  }
 }

 const handleResolveSubmit = async () => {
  setLoading(true)

  const res = await updateTicketApi(ticketId, {
   status: 'RESOLVED',
   resolution_comment: resolutionComment.trim(),
  })

  setLoading(false)

  if (res.success) {
   setResolveModal(false)
   setResolutionComment('')
   navigate('/all-tickets')
  }
 }

 useEffect(() => {
  if (!ticketId) return

  setLoading(true)
  void getTicketDetailApi(ticketId).then((res) => {
   if (res.success) {
    setData(res.data)
   }
   setLoading(false)
  })
 }, [ticketId])

 return (
  <>
   {loading && (
    <div className='fixed inset-0 z-[9999] flex items-center justify-center bg-black/30'>
     <Spinner />
    </div>
   )}

   <div className='mt-6'>
    <div className='grid grid-cols-[180px_1fr] gap-y-4 text-sm'>
     <div className='text-gray-500'>
      {' '}
      {t('ticket.ticketDetailScreen.ticketID')}
     </div>
     <div className='font-medium'>
      {data?.ticket_number}
     </div>

     <div className='text-gray-500'>
      {t('ticket.ticketDetailScreen.ticketDate')}
     </div>
     <div>{data && formatDate(data.ticket_date)}</div>

     {userCategoryKey !== ROLE_GROUPS.DISTRIBUTOR && (
      <>
       {' '}
       <div className='text-gray-500'>
        {' '}
        {t('ticket.ticketDetailScreen.ticketRaisedBy')}
       </div>
       <div>{data?.ticket_raised_by}</div>
       <div className='text-gray-500'>
        {t('ticket.ticketDetailScreen.phoneNumber')}
       </div>
       <div>
        {data?.phone_number ? data?.phone_number : '--'}
       </div>
       {data?.customer_name !== data?.ticket_raised_by && (
        <>
         <div className='text-gray-500'>
          {t('ticket.ticketDetailScreen.ticketRaisedFor')}
         </div>
         <div>{data?.customer_name}</div>
        </>
       )}
       <div className='text-gray-500'>
        {t('ticket.ticketDetailScreen.userType')}
       </div>
       <div>{data?.user_type ? data?.user_type : '--'}</div>
      </>
     )}

     <div className='text-gray-500'>
      {t('ticket.ticketDetailScreen.module')}
     </div>
     <div>{data?.module ? data?.module : '--'}</div>

     <div className='text-gray-500'>
      {t('ticket.ticketDetailScreen.issueType')}
     </div>
     <div>{data?.issue_type ? data.issue_type : '--'}</div>

     <div className='text-gray-500'>
      {t('ticket.ticketDetailScreen.issueDescription')}
     </div>
     <div>
      {data?.issue_description
       ? data.issue_description
       : '--'}
     </div>

     <div className='text-gray-500'>
      {t('ticket.ticketDetailScreen.issuePriority')}
     </div>
     <div>{data?.priority ? data.priority : '--'}</div>

     <div className='text-gray-500'>
      {t('ticket.ticketDetailScreen.ticketStatus')}
     </div>
     <div className='flex items-center gap-2'>
      <span
       className={`font-medium ${
        isOpen || isReopen
         ? 'text-yellow-700'
         : 'text-green-700'
       }`}
      >
       {isReopen ? 'REOPEN' : data?.ticket_status}
      </span>
     </div>

     {isReopen && (
      <>
       <div className='text-gray-500'>
        {t('ticket.ticketDetailScreen.reopenReason')}
       </div>
       <div className='flex items-center gap-2'>
        <span>
         {data?.reopen_reason ? data.reopen_reason : '--'}
        </span>
       </div>
      </>
     )}

     <div className='text-gray-500'>
      {t('ticket.ticketDetailScreen.updatedOn')}
     </div>
     <div>
      {data?.updated_on
       ? formatDate(data.updated_on)
       : '--'}
     </div>

     {userCategoryKey !== ROLE_GROUPS.DISTRIBUTOR && (
      <>
       <div className='text-gray-500'>
        {t('ticket.ticketDetailScreen.userFeedback')}
       </div>

       <div
        className={`flex items-center  gap-2  font-medium ${
         !data?.feedback_status
          ? 'text-black'
          : data.feedback_status === 'SATISFIED'
          ? 'text-green-700'
          : 'text-red-700'
        }`}
       >
        {data?.feedback_status
         ? data.feedback_status === 'SATISFIED'
           ? 'SATISFIED'
           : 'NOT SATISFIED'
         : 'Yet to respond'}
       </div>
      </>
     )}

     {data?.resolution_comment?.trim() && (
      <>
       <div className='text-gray-500'>
        {t('ticket.ticketDetailScreen.resolutionComment')}
       </div>
       <div>{data.resolution_comment}</div>
      </>
     )}
    </div>
    <hr className='mb-6 mt-6' />

    {data && data.attachments?.length > 0 && (
     <div className='mt-8'>
      <div className='text-sm font-medium mb-4'>
       {t('ticket.ticketDetailScreen.attachments')}
      </div>

      <div className='flex gap-4 flex-wrap'>
       {data.attachments.map((item, index) => (
        <div
         key={index}
         className='relative w-[120px] cursor-pointer'
         onClick={() =>
          setPreview({
           id: index.toString(),
           url: item.url,
           type: item.type as 'image' | 'video',
          })
         }
        >
         {item.type === 'image' && (
          <img
           src={item.url}
           alt='attachment'
           className='w-full h-[90px] object-cover border rounded-md'
           onError={(e) => {
            e.currentTarget.src = '/placeholder-image.png'
           }}
          />
         )}

         {item.type === 'video' && (
          <div className='relative'>
           <video
            src={item.url}
            className='w-full h-[90px] object-cover border rounded-md'
            muted
            playsInline
            preload='metadata'
           />

           <div className='absolute inset-0 flex items-center justify-center bg-black/40 rounded-md'>
            <span className='text-white text-2xl'>▶</span>
           </div>
          </div>
         )}
        </div>
       ))}
      </div>
     </div>
    )}
    {shouldShowResolutionFeedback && (
     <ResolutionFeedback
      ticketNumber={data.ticket_number}
      feedbackStatus={data.feedback_status}
      user_feedback_comment={data.user_feedback_comment}
     />
    )}

    {data && data.attachments?.length > 0 && (
     <hr className='mb-6 mt-6' />
    )}

    {(isOpen || isReopen) &&
     userCategoryKey === ROLE_GROUPS.TSU && (
      <div className='flex justify-end mt-10'>
       <button
        className='bg-[#4E83E4] text-white px-6 py-2 rounded-md text-sm font-medium hover:bg-blue-700'
        onClick={() => setResolveModal(true)}
       >
        {t('ticket.ticketDetailScreen.markResolved')}
       </button>
      </div>
     )}
    {isResolved && userCategoryKey === ROLE_GROUPS.TSU && (
     <div className='flex justify-end mt-10'>
      <button
       className='border border-red-500 text-red-500 bg-red-50 px-6 py-2 rounded-md text-sm font-semibold hover:bg-red-100'
       onClick={handleReOpenClick}
      >
       {t('ticket.ticketDetailScreen.reopenTicket')}
      </button>
     </div>
    )}
    {isResolved &&
     (userCategoryKey === ROLE_GROUPS.PMU ||
      userCategoryKey === ROLE_GROUPS.DISTRIBUTOR) && (
      <>
       <div className='flex flex-row justify-end mt-10 gap-6'>
        <button
         className='border border-red-500 text-red-500 bg-red-50 px-6 py-2 rounded-md text-sm font-semibold hover:bg-red-100'
         onClick={handleReOpenClick}
        >
         {t('ticket.ticketDetailScreen.reopenTicket')}
        </button>
        {!isClosed && (
         <button
          className='bg-[#4E83E4] text-white px-6 py-2 rounded-md text-sm font-medium hover:bg-blue-700'
          onClick={async () => {
           setLoading(true)

           const res = await updateTicketApi(ticketId, {
            status: 'CLOSED',
           })

           setLoading(false)

           if (res.success) {
            navigate(-1)
           }
          }}
         >
          {t('ticket.ticketDetailScreen.closeTicket')}
         </button>
        )}
       </div>
      </>
     )}

    <ReopenTicketModal
     isOpen={reOpenModal}
     onClose={() => setReOpenModal(false)}
     remark={remark}
     setRemark={setRemark}
     onSubmit={handleReOpenSubmit}
    />
    <ResolutionCommentModal
     isOpen={resolveModal}
     onClose={() => setResolveModal(false)}
     comment={resolutionComment}
     setComment={setResolutionComment}
     onSubmit={handleResolveSubmit}
    />
    {preview && (
     <div className='fixed inset-0 z-[999] flex items-center justify-center bg-black/80'>
      <button
       className='absolute top-6 right-6 text-white text-3xl'
       onClick={() => setPreview(null)}
      >
       ✕
      </button>

      {preview.type === 'image' ? (
       <img
        src={preview.url}
        alt='preview'
        className='max-h-[90vh] max-w-[90vw] rounded-lg object-contain'
       />
      ) : (
       <video
        src={preview.url}
        controls
        autoPlay
        playsInline
        className='max-h-[90vh] max-w-[90vw] rounded-lg bg-black'
       />
      )}
     </div>
    )}
   </div>
  </>
 )
}

export default TicketDetailData
